<template>
  <f7-block>
    <f7-block-title>{{ $t('adminDashboard.platformOverview') }}</f7-block-title>
    <div class="metric-grid">
      <div class="metric-card bg-blue">
        <f7-icon f7="person_2" size="20" class="metric-icon" />
        <span class="metric-lbl">{{ $t('adminDashboard.totalUsers') }}</span>
        <span class="metric-val">{{ fmt(data.totalUsers) }}</span>
      </div>
      <div class="metric-card bg-teal">
        <f7-icon f7="person_3" size="20" class="metric-icon" />
        <span class="metric-lbl">{{ $t('adminDashboard.totalGroups') }}</span>
        <span class="metric-val">{{ fmt(data.totalGroups) }}</span>
      </div>
      <div class="metric-card bg-orange">
        <f7-icon f7="megaphone" size="20" class="metric-icon" />
        <span class="metric-lbl">{{ $t('adminDashboard.totalCampaigns') }}</span>
        <span class="metric-val">{{ fmt(data.totalCampaigns) }}</span>
      </div>
      <div class="metric-card bg-green">
        <f7-icon f7="checkmark_circle_fill" size="20" class="metric-icon" />
        <span class="metric-lbl">{{ $t('adminDashboard.activeCampaigns') }}</span>
        <span class="metric-val">{{ fmt(data.activeCampaigns) }}</span>
      </div>
      <div class="metric-card bg-purple">
        <f7-icon f7="chart_bar_square" size="20" class="metric-icon" />
        <span class="metric-lbl">{{ $t('adminDashboard.totalRevenue') }}</span>
        <span class="metric-val">{{ fmtCfa(data.totalRevenue) }}</span>
      </div>
      <div class="metric-card bg-pink">
        <f7-icon f7="chart_line_uptrend" size="20" class="metric-icon" />
        <span class="metric-lbl">{{ $t('adminDashboard.dailyRevenue') }}</span>
        <span class="metric-val">{{ fmtCfa(data.dailyRevenue) }}</span>
      </div>
    </div>

    <div v-if="data.revenueGrowthRate != null" class="trend-row">
      <span>{{ $t('adminDashboard.revenueGrowth') }}</span>
      <span class="trend-pill" :class="trendClass(data.revenueGrowthRate)">
        <f7-icon :f7="trendIcon(data.revenueGrowthRate)" size="12" />
        {{ pct(data.revenueGrowthRate) }}
      </span>
    </div>
  </f7-block>
</template>

<script setup>
const props = defineProps({ data: { type: Object, required: true } });

function fmt(n) {
  if (n == null) return '-';
  return Math.round(n).toLocaleString('fr-FR');
}
function fmtCfa(v) {
  if (v == null) return '-';
  const n = parseFloat(v) || 0;
  return n.toLocaleString('fr-FR', { minimumFractionDigits: 0, maximumFractionDigits: 0 }) + ' FCFA';
}
function pct(v) {
  if (v == null) return '-';
  return v.toFixed(1) + '%';
}
function trendClass(v) {
  if (v > 0) return 'trend-up';
  if (v < 0) return 'trend-down';
  return 'trend-neutral';
}
function trendIcon(v) {
  if (v > 0) return 'arrow_up';
  if (v < 0) return 'arrow_down';
  return 'minus';
}
</script>

<style scoped>
.metric-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
  gap: 10px;
}
.metric-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 4px;
  padding: 14px 8px;
  border-radius: 12px;
  background: var(--f7-list-bg-color, #f5f5f5);
}
.metric-card .metric-icon { color: var(--f7-on-surface-variant, #8e8e93); }
.bg-blue .metric-icon { color: #2196f3; }
.bg-teal .metric-icon { color: #009688; }
.bg-orange .metric-icon { color: #ff9800; }
.bg-green .metric-icon { color: #4caf50; }
.bg-purple .metric-icon { color: #9c27b0; }
.bg-pink .metric-icon { color: #e91e63; }
.metric-lbl { font-size: 11px; color: var(--f7-on-surface-variant, #8e8e93); text-align: center; line-height: 1.3; }
.metric-val { font-size: 18px; font-weight: 700; color: var(--f7-text-color, #333); }
.trend-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 10px;
  padding: 8px 12px;
  border-radius: 8px;
  background: var(--f7-list-bg-color, #f5f5f5);
  font-size: 13px;
  font-weight: 500;
}
.trend-pill {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 10px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
}
.trend-up { background: color-mix(in srgb, #4caf50 12%, transparent); color: #4caf50; }
.trend-down { background: color-mix(in srgb, #f44336 12%, transparent); color: #f44336; }
.trend-neutral { background: var(--f7-list-bg-color, #f5f5f5); color: var(--f7-on-surface-variant, #8e8e93); }
</style>